/**
 * Pegado inteligente.
 *
 * El caso real: el autor copia un bloque de ChatGPT, de Word o de Google Docs y
 * lo pega dentro del artículo. Sin esto, TipTap trae el HTML tal cual —incluidos
 * `<h1>`, estilos inline, `class` y spans de colores— y el renderer los
 * descarta: el autor pierde el formato que sí tenía a la vista.
 *
 * Por eso el pegado pasa por un transformador antes de llegar al documento.
 * Es una allowlist, igual que el renderer: se conserva la estructura semántica
 * (encabezados, listas, énfasis, enlaces, citas) y se descarta el ruido
 * (estilos, clases, scripts, iframes).
 */

import { looksLikeMarkdown, markdownToHtml } from "@vimos/rich-content";
import { isSafeImageTarget, isSafeLinkTarget } from "./extensions";

export interface PasteResult {
  /**
   * HTML ya saneado para insertar. Vacío cuando la única vía fue reconstruir
   * desde texto plano: en ese caso `structuredText` trae el material.
   */
  html: string;
  /** Texto plano del portapapeles cuando conviene estructurarlo. */
  structuredText?: string;
  /** Se informa al autor qué se normalizó, para que no sea una sorpresa. */
  notes: string[];
}

const ALLOWED_TAGS = new Set([
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "code",
  "pre",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ul",
  "ol",
  "li",
  "blockquote",
  "a",
  "img",
  "hr",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
]);

/** Etiquetas cuyo contenido se conserva pero se desenvuelve. */
const UNWRAP_TAGS = new Set([
  "div",
  "span",
  "section",
  "article",
  "main",
  "body",
  "html",
  "font",
  "figure",
  "figcaption",
  "mark",
]);

/** Contenedores que se eliminan por completo, incluido su contenido. */
const DROP_WITH_CONTENT = new Set([
  "script",
  "style",
  "noscript",
  "iframe",
  "object",
  "embed",
  "form",
  "svg",
  "canvas",
  "video",
  "audio",
  "input",
  "button",
]);

/**
 * Headings pegados que se degradan: el H1 lo genera el título del post.
 * Un H3 pegado se mantiene como H3 — degradarlo perdería la jerarquía real del
 * documento de origen.
 */
const HEADING_MAP: Record<string, 2 | 3> = { h1: 2, h2: 2, h3: 3, h4: 3, h5: 3, h6: 3 };

const ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  hellip: "…",
  mdash: "—",
  ndash: "–",
  rsquo: "\u2019",
  lsquo: "\u2018",
  ldquo: "\u201c",
  rdquo: "\u201d",
};

function decodeEntities(value: string): string {
  return value.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (match, entity: string) => {
    const key = entity.toLowerCase();
    if (key.startsWith("#x")) {
      const code = Number.parseInt(key.slice(2), 16);
      return Number.isFinite(code) ? String.fromCodePoint(code) : match;
    }
    if (key.startsWith("#")) {
      const code = Number.parseInt(key.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : match;
    }
    return ENTITIES[key] ?? match;
  });
}

/**
 * DOMParser solo existe en browser. El editor es una isla de React que ya vive
 * en browser, así que la dependencia es aceptable; aun así el fallback evita
 * romper si se llega a llamar desde otro contexto.
 */
function parse(html: string): Document | null {
  if (typeof DOMParser === "undefined") return null;
  return new DOMParser().parseFromString(html, "text/html");
}

/**
 * Nodo de texto.
 *
 * El detalle importante: los espacios **no** se recortan. Un `Hola <strong>mundo</strong>` pegado
 * desde Word llega como tres nodos; recortar cada uno produciría "Holamundo".
 * Solo se quitan los espacios de los extremos, donde no significan nada.
 */
function cleanText(node: Node, index: number, total: number): Node[] {
  const raw = node.nodeValue ?? "";
  if (!raw) return [];

  const collapsed = raw.replace(/\s+/g, " ");
  if (!collapsed) return [];

  const isFirst = index === 0;
  const isLast = index === total - 1;

  let value = collapsed;
  if (isFirst) value = value.replace(/^\s+/, "");
  if (isLast) value = value.replace(/\s+$/, "");

  if (!value) return [];

  return [document.createTextNode(decodeEntities(value))];
}

function clean(node: Node, notes: Set<string>): Node[] {
  if (node.nodeType === 3) {
    return cleanText(node, 0, 1);
  }

  if (node.nodeType !== 1) return [];
  const element = node as Element;
  const tag = element.tagName.toLowerCase();

  if (DROP_WITH_CONTENT.has(tag)) {
    if (tag === "script" || tag === "style") notes.add("Se descarto codigo incrustado al pegar");
    return [];
  }

  if (UNWRAP_TAGS.has(tag)) {
    return Array.from(element.childNodes).flatMap((child) => clean(child, notes));
  }

  // <p> que solo envuelve un bloque: se desenvuelve para no crear un párrafo
  // vacío alrededor del encabezado o la lista.
  if (tag === "p") {
    const hasBlock = Array.from(element.childNodes).some((child) => {
      if (child.nodeType !== 1) return false;
      const childTag = (child as Element).tagName.toLowerCase();
      return childTag.startsWith("h") || ["ul", "ol", "blockquote", "table", "pre"].includes(childTag);
    });
    if (hasBlock) {
      return Array.from(element.childNodes).flatMap((child) => clean(child, notes));
    }
  }

  if (!ALLOWED_TAGS.has(tag)) {
    return Array.from(element.childNodes).flatMap((child) => clean(child, notes));
  }

  const children = Array.from(element.childNodes);

  if (tag === "img") {
    const src = (element.getAttribute("src") ?? "").trim();
    if (!isSafeImageTarget(src)) {
      notes.add("Se omitio una imagen con origen no permitido");
      return [];
    }
    const img = document.createElement("img");
    img.setAttribute("src", src);
    const alt = element.getAttribute("alt")?.trim();
    if (alt) img.setAttribute("alt", alt);
    return [img];
  }

  if (tag === "a") {
    const href = (element.getAttribute("href") ?? "").trim();
    if (!isSafeLinkTarget(href)) {
      notes.add("Se removio un enlace con destino no permitido");
      return children.flatMap((child) => clean(child, notes));
    }
    const anchor = document.createElement("a");
    anchor.setAttribute("href", href);
    // Se conservan los hijos: un enlace puede llevar negrita o código dentro.
    for (const child of children) {
      for (const cleaned of clean(child, notes)) anchor.appendChild(cleaned);
    }
    if (!anchor.textContent?.trim()) return [];
    return [anchor];
  }

  const headingMatch = HEADING_MAP[tag];
  if (headingMatch) {
    if (tag === "h1") notes.add("El H1 pegado se guardo como H2, porque el titulo ya es el H1");
    if (["h4", "h5", "h6"].includes(tag)) notes.add("Los subtitulos H4 a H6 se guardaron como H3");
    const heading = document.createElement(`h${headingMatch}`);
    const value = collapse(decodeEntities(element.textContent ?? ""));
    if (!value) return [];
    heading.textContent = value;
    return [heading];
  }

  const cleanTag = tag === "b" ? "strong" : tag === "i" ? "em" : tag;
  const out = document.createElement(cleanTag);

  // <pre> mantiene el texto literal: no se limpian los espacios internos.
  if (cleanTag === "pre") {
    const text = decodeEntities(element.textContent ?? "");
    if (!text.trim()) return [];
    out.textContent = text;
    return [out];
  }

  children.forEach((child, index) => {
    const cleaned = child.nodeType === 3 ? cleanText(child, index, children.length) : clean(child, notes);
    for (const node of cleaned) out.appendChild(node);
  });

  // Una lista o tabla vacía no sirve de nada en el documento.
  if (["li", "ul", "ol", "table", "tr", "thead", "tbody"].includes(cleanTag) && out.childNodes.length === 0)
    return [];
  if (["ul", "ol"].includes(cleanTag) && out.querySelector("li") === null) return [];

  // Un párrafo que se quedó sin contenido solo porque se descartó lo que había
  // dentro (una imagen con origen no permitido, por ejemplo) no debe llegar al
  // documento como una caja vacía.
  if (["p", "li", "td", "th"].includes(cleanTag) && out.textContent?.trim() === "") {
    const keepsShape = out.querySelector("img, br, hr, table, ul, ol, blockquote, pre");
    if (!keepsShape) return [];
  }

  return [out];
}

function collapse(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

/**
 * Normaliza HTML pegado de una fuente externa.
 *
 * Devuelve `null` cuando no hay nada utilizable, para que el editor aplique su
 * comportamiento de pegado por defecto en lugar de insertar vacío.
 */
export function sanitizePastedHtml(html: string): PasteResult | null {
  const source = html.trim();
  if (!source) return null;

  const parsed = parse(source);
  if (!parsed) return null;

  const notes = new Set<string>();
  const container = document.createElement("div");

  for (const child of Array.from(parsed.body.childNodes)) {
    for (const cleaned of clean(child, notes)) container.appendChild(cleaned);
  }

  const result = container.innerHTML.trim();
  if (!result) return null;

  return { html: result, notes: [...notes] };
}

const MARKDOWN_HEADING = /^#{1,6}\s+\S/m;
const LIST_MARKER = /^\s*(?:[-*•]|\d+[.)])\s+\S/m;

/**
 * Etiquetas que indican que el HTML trae formato real (Google Docs, Word,
 * ChatGPT en modo rich text). `div`, `p`, `br`, `pre` y `code` no cuentan:
 * un envoltorio plano alrededor de Markdown literal no debe impedir la
 * conversion.
 */
const SEMANTIC_HTML = /<(?:h[1-6]|ul|ol|li|strong|em|s|u|a|table|blockquote|img|hr)\b/i;

function hasSemanticFormatting(html: string | undefined): boolean {
  return html !== undefined && SEMANTIC_HTML.test(html);
}

/**
 * Normaliza el portapapeles completo (texto + HTML) y devuelve lo que debe
 * insertarse.
 *
 * Orden de preferencia:
 * 1. Markdown en texto plano (articulo de ChatGPT copiado como texto, archivo
 *    .md): se convierte a elementos reales aunque venga un HTML plano.
 * 2. HTML enriquecido saneado (Word, Google Docs, ChatGPT rich text).
 * 3. Texto con estructura reconocible: se reconstruye desde ahi.
 */
export function normalizePaste(clipboard: { html: string; text: string }): PasteResult | null {
  const sanitized = sanitizePastedHtml(clipboard.html);
  const text = clipboard.text.trim();

  if (text && looksLikeMarkdown(text) && !hasSemanticFormatting(sanitized?.html)) {
    const converted = sanitizePastedHtml(markdownToHtml(text));
    if (converted) {
      return {
        ...converted,
        notes: [...converted.notes, "Se convirtio el Markdown pegado en elementos del editor"],
      };
    }
  }

  if (sanitized) return sanitized;

  if (text && (MARKDOWN_HEADING.test(text) || LIST_MARKER.test(text))) {
    return {
      html: "",
      structuredText: text,
      notes: ["El portapapeles no traia formato; se reconstruyo desde la estructura del texto"],
    };
  }

  return null;
}
